/**
 * Sentry browser SDK init for the pro admin frontend.
 *
 * Reports to the `pro-web` Sentry project — a DISTINCT project from
 * pro's functions, which report via `functions/src/lib/sentry.ts` to
 * their own DSN. Never share the DSN between the two: crossing them
 * would bury the auth-email incidents KB-2359 exists to surface.
 *
 * Imported as the FIRST line of `main.tsx` — before React, before
 * `./App.tsx`, before anything else. This is load-bearing, not style:
 * ES module imports evaluate depth-first in source order, before the
 * importing module's own body runs. `App.tsx` statically imports
 * `AuthContext.tsx`, which statically imports `services/firebaseService.ts`,
 * whose `initializeApp`/`getAuth`/`getFirestore` calls run UNGUARDED at
 * module scope. If any of that throws during evaluation (bad config, a
 * duplicate-app collision, anything), the whole module graph — including
 * a later `initSentry()` call sitting in `main.tsx`'s own body — never
 * runs, and the failure is invisible: white screen, nothing in Sentry.
 * Importing this file first, with the init as ITS OWN top-level side
 * effect, arms Sentry before any of that can happen (found at
 * `/code-review high`, KB-2604 round 1).
 *
 * Adapted from the marketing `@sentry/astro` pattern (KB-228) to
 * `@sentry/react`:
 *
 *   - DSN injected at build time via `VITE_SENTRY_DSN`. No DSN ⇒
 *     complete no-op — pro's wip/test/dev builds may run without one.
 *   - `dataCollection` pinned to the v10 restrictive baseline (Sentry v11
 *     replaced `sendDefaultPii: false` with it, and an UNSET
 *     `dataCollection` now collects user info, cookies and bodies by
 *     default) — never send email or IP. UID-only
 *     identification is wired separately in `AuthContext` (a sibling
 *     ticket), not here.
 *   - `environment` derived from `VITE_ENVIRONMENT` (CI-injected), never
 *     `window.location.hostname` — see `sentry-environment.ts`.
 *   - `release` = the deployed commit (`VITE_COMMIT_ID`, already
 *     CI-injected).
 *   - Performance tracing is ON (KB-4773): one transaction per page load and
 *     per navigation, named by route PATTERN (`/organizations/:id/edit`),
 *     never by the concrete id. The names come from `SentryRoutes`
 *     (`sentry-routes.ts`), which `App.tsx` renders instead of `<Routes>`.
 *   - Session replay is deliberately OFF — it needs its own privacy decision.
 */

import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { resolveSentryEnvironment } from './sentry-environment';
import { SENTRY_DENY_URLS } from './sentry-deny-urls';

const DEFAULT_TRACES_SAMPLE_RATE = 1;

/**
 * `VITE_SENTRY_TRACES_SAMPLE_RATE` as a rate in [0, 1]. Unset or blank means
 * the default (1.0 — pro traffic is small and every page is wanted). A value
 * that is not a number in range also falls back to the default rather than to
 * 0, so a typo cannot silently switch tracing off.
 */
export function resolveTracesSampleRate(raw: string | undefined): number {
  if (raw === undefined || raw.trim() === '') return DEFAULT_TRACES_SAMPLE_RATE;
  const rate = Number(raw);
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) return DEFAULT_TRACES_SAMPLE_RATE;
  return rate;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: resolveSentryEnvironment(import.meta.env.VITE_ENVIRONMENT),
    release: import.meta.env.VITE_COMMIT_ID || undefined,
    // v10's `sendDefaultPii: false`, spelled out for v11 (Sentry MIGRATION.md,
    // 10.x -> 11.x, "keep the v10 default behavior").
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      },
      httpBodies: [],
      urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
    },
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: resolveTracesSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    // pro-functions is not traced, so no sentry-trace / baggage header needs to
    // leave the page; an empty list also keeps them off cross-origin calls to
    // Firebase and Google APIs, whose CORS preflights would reject them.
    tracePropagationTargets: [],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    denyUrls: SENTRY_DENY_URLS,
  });
}

// Fires as a side effect of merely IMPORTING this module — not of calling
// initSentry() explicitly. That's what lets `main.tsx` arm Sentry just by
// importing this file first; see the module doc comment above.
initSentry();
